import React, {useState} from 'react';

import styled from 'styled-components';

import {
  createFormErrorsContext,
  FormControlGroup,
  FormErrorsProps,
} from 'app/general/forms';
import {ItemWithChildrenStructure} from 'app/general/structures/ItemWithChildrenStructure';
import {KBTreeEditItemStructure} from 'app/kbs/components/KBEditPage/KBTree/KBTreeEditItemStructure';
import {
  KBTreeItemInnerButton,
  KBTreeItemInnerDiv,
} from 'app/kbs/components/KBEditPage/KBTree/KBTreeStructures';
import {SearchItemsSelectSingle} from 'app/search/components/SearchItemsSelect';

import type {SearchSerializedType} from 'sweet-types';

type ExcludeParameterType =
  | {exclude_folder_id: number | string}
  | {exclude_category_id: number | string}
  | {exclude_quiz_id: string};

type OwnProps = {
  buttonDescription: string;
  placeholderText: string;
  contentTypes: Array<'procedure' | 'policy' | 'proceduregroup'>;
  excludeParameter: ExcludeParameterType;
  afterAction?: () => unknown;
  create: (item: SearchSerializedType) => Promise<unknown>;
};
type Props = OwnProps & FormErrorsProps;
export const KBTreeCreateArticleItem = createFormErrorsContext<Props>(
  ({
    create,
    captureFormErrorsFromRejection,
    afterAction,
    buttonDescription,
    placeholderText,
    contentTypes,
    excludeParameter,
  }: Props) => {
    const [editing, setEditing] = useState<boolean>(false);

    function doCreate(value: SearchSerializedType) {
      return create(value).then(() => {
        setEditing(false);
        afterAction && afterAction();
      }, captureFormErrorsFromRejection);
    }

    return (
      <ItemWithChildrenStructure>
        {editing ? (
          <KBTreeItemInnerDiv
            toggleableIcon={<i className="fas fa-plus-circle" />}
            className="p-0"
          >
            <CreateItemForm
              onSubmit={doCreate}
              onCancel={() => setEditing(false)}
              excludeParameter={excludeParameter}
              contentTypes={contentTypes}
              placeholderText={placeholderText}
            />
          </KBTreeItemInnerDiv>
        ) : (
          <KBTreeItemInnerButton
            className="btn-link btn-sm"
            itemIcon={<i className="fas fa-plus-circle" />}
            onClick={() => setEditing(true)}
          >
            {buttonDescription}
          </KBTreeItemInnerButton>
        )}
      </ItemWithChildrenStructure>
    );
  }
);
const StyledCreateItemDiv = styled.div`
  flex-grow: 1;

  & > div > div {
    border-radius: 0.25rem 0 0 0.25rem;

    @media (max-width: 575px) {
      border-bottom: none;
      border-radius: 0.25rem 0.25rem 0 0;
    }
  }
`;
type CreateItemFormProps = {
  onCancel: () => void;
  onSubmit: (searchData: SearchSerializedType) => Promise<unknown>;
  placeholderText: string;
  contentTypes: Array<'procedure' | 'policy' | 'proceduregroup'>;
  excludeParameter?: ExcludeParameterType;
};
export function CreateItemForm({
  onCancel,
  onSubmit,
  placeholderText,
  contentTypes,
  excludeParameter,
}: CreateItemFormProps) {
  const [value, setValue] = useState<SearchSerializedType | null>(null);
  return (
    <FormControlGroup field="document_object_id" className="flex-grow-1 mb-0">
      <KBTreeEditItemStructure
        disabled={value == null}
        onCancel={onCancel}
        onSubmit={() =>
          value != null
            ? onSubmit(value).then(() => setValue(null))
            : Promise.resolve(true)
        }
      >
        {(saving) => (
          <StyledCreateItemDiv>
            <SearchItemsSelectSingle
              disabled={saving}
              selectProps={{
                autoFocus: true,
                isClearable: false,
              }}
              placeholder={placeholderText}
              params={{
                ct: contentTypes.join(','),
                ordering: 'name',
                ...excludeParameter,
              }}
              refetchDataOnOpen={true}
              selected={value}
              onChange={(value) => Promise.resolve(setValue(value))}
            />
          </StyledCreateItemDiv>
        )}
      </KBTreeEditItemStructure>
    </FormControlGroup>
  );
}
